import { Component, getElement, h, Host, Listen, Method, Prop, State, Watch, Element } from '@stencil/core';
import { Provides } from '@sydle/sydle-ui';
import { SySiteI18n } from '../sy-site-i18n/sy-site-i18n';
import { i18nUpdateComponent } from '../sy-site-i18n/sy-site-i18n-hooks';
import * as dataEN from './assets/data/data.en.json';
import * as dataES from './assets/data/data.es.json';
import * as dataPT from './assets/data/data.pt.json';

const STATIC_DATA = {
  en: dataEN,
  es: dataES,
  pt: dataPT,
};

function getStaticData(lang: string) {
  return STATIC_DATA[lang] || STATIC_DATA.en;
}

@Component({
  tag: 'sy-site-shell',
  styleUrl: 'sy-site-shell.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class SySiteShell {
  private authResolve = null;
  private isAuthReady = new Promise(resolve => {
    this.authResolve = resolve;
  });

  @Element() el: HTMLSySiteShellElement;
  @i18nUpdateComponent() @Provides('i18next:instance') @State() siteI18n: HTMLSySiteI18nElement;
  @Provides('i18next:language') @State() lang = null;
  @Provides('i18next:t') @State() t = null;
  @Provides('siteApi') @State() siteApi: HTMLSySiteApiElement;
  @Provides('siteAuth') @State() siteAuth: HTMLSySiteAuthElement;
  @State() sidebarProps: any;
  @State() footerProps: any;
  @State() loadSidebar = false;
  @State() sidebar: HTMLSySiteSidebarElement;

  /**
   * Enable to use backlink
   */
  @Prop() backLink = false;

  /**
   * Link that backLink leads to
   */
  @Prop() backLinkHref;

  /**
   * Title of cta header
   */
  @Prop() ctaTitle;

  /**
   * Icon of cta header
   */
  @Prop() ctaIcon;

  /**
   * Link that CTA leads to
   */
  @Prop() ctaHref;

  /**
   * Enable to use blog search button
   */
  @Prop() searchButton = false;

  /**
   * Controls user tab visibility
   */
  @Provides("hideUserTab") @Prop() hideUserTab = false;

  /**
   * Calls internal sidebar close function
   */
  @Method()
  async header_close() {
    getElement(this)?.shadowRoot.querySelector('sy-site-header')?.shadowRoot.querySelector('sy-site-burger-icon').close();
  }

  @Watch('lang')
  langWatcher(): void {
    this.siteApi.getMenuConfig().then(config => {
      this.sidebarProps = config.menuEntries;
      this.footerProps = config.footerEntries;
    });
  }

  private handleI18nRef(siteI18n) {
    this.siteI18n = siteI18n;
    this.siteI18n.whenReady().then(() => {
      this.lang = this.siteI18n.i18n.language;
      this.t = this.siteI18n.i18n.t;
    });

    SySiteI18n.i18n.on('languageChanged', lang => {
      this.lang = lang;
      this.t = this.siteI18n.i18n.t;
    });
  }

  private handleAuthRef(auth) {
    this.siteAuth = auth;
    this.authResolve();
  }

  @Listen('sySiteBurgerIconDidOpen')
  openSidebar(): void {
    this.loadSidebar = true;
    if (this.sidebar) this.sidebar.show();
  }

  @Listen('sySiteBurgerIconDidClose')
  closeSidebar(): void {
    this.sidebar.hide();
  }

  @Listen('sySiteSidebarDidShow')
  disableScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  @Listen('sySiteSidebarDidHide')
  enableScroll(): void {
    document.body.style.overflow = 'auto';
  }

  @Listen('sySiteLanguageHeaderDidOpen')
  shiftSidebarDown(): void {
    this.sidebar.shift('97');
  }

  @Listen('sySiteLanguageHeaderDidClose')
  shiftSidebarUp(): void {
    this.sidebar.shift('53');
  }

  /**
   * Method to get site API
   */
  @Method()
  async getSiteApi(): Promise<any> {
    return this.siteApi;
  }

  /**
   * Method to get site auth
   */
  @Method()
  async getSiteAuth(): Promise<any> {
    await this.isAuthReady;
    return this.siteAuth;
  }

  private handleOnLoad(): void {
    this.el.shadowRoot.querySelector('sy-site-footer').style.visibility = 'inherit';
  }

  componentDidLoad() {
    setTimeout(() => this.handleOnLoad(), 2000);

    const options = {
      root: null,
      threshold: 0,
    };
    const target = this.el.shadowRoot.querySelector('sy-site-footer');
    const observer = new IntersectionObserver(() => {
      this.handleOnLoad();
    }, options);
    setTimeout(() => observer.observe(target), 1000);
  }

  render() {
    return (
      <Host>
        <sy-site-api ref={el => (this.siteApi = el)} />

        {this.siteApi && <sy-site-i18n ref={el => this.handleI18nRef(el)} siteApi={this.siteApi} portalId="5eab2bccc7fa677196cde228" />}

        {this.siteI18n && (
          <sy-site-auth
            ref={el => this.handleAuthRef(el)}
            component="sy-site-sign-in-wall"
            loginComponentProps={{
              supportedNetworks: ['facebook', 'google', 'linkedin'],
            }}
          />
        )}

        {this.siteApi && this.siteI18n && <sy-site-language-header siteApi={this.siteApi} siteI18n={this.siteI18n} />}

        {this.siteI18n && this.siteAuth && (
          <sy-site-header
            cta-title={this.ctaTitle}
            cta-href={this.ctaHref}
            cta-icon={this.ctaIcon}
            search-button={this.searchButton}
            back-link={this.backLink}
            back-link-href={this.backLinkHref}
            hide-user-tab={this.hideUserTab}
          />
        )}

        <sy-site-sidebar ref={el => (this.sidebar = el)} entries={this.sidebarProps} />

        <sy-site-alert-modal />
        <slot></slot>
        <sy-site-footer categories={this.footerProps} policyLinks={getStaticData(this.lang).policyLinks} style={{ visibility: 'hidden' }} />
      </Host>
    );
  }
}
